import { useState, useRef, useEffect } from "react";
import { Slider } from "@/components/ui/slider";
import Amount from "utils/amount";
import "slot-text/style.css";
import { SlotText } from "slot-text/react";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import type { PurchaseFrequency } from "../types/onboarding-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ArrowRight, Lightbulb } from "lucide-react";

export type { PurchaseFrequency };

/**
 * Public contract for the StepFive per-purchase spend slider.
 *
 * The component is fully controlled by the parent page:
 *   - `value` is the current per-purchase spend in CLP, or `null` when
 *     the user has not interacted with the slider yet. The page
 *     initializes it from `onboardingData.spend_per_purchase` so a
 *     remount resumes with the persisted choice.
 *   - `onValueChange` is called with a non-null number whenever the user
 *     moves the slider. The page updates its draft and clears any stale
 *     validation error in the same callback.
 *   - `purchaseFrequency` is the persisted step-4 selection (or `null`
 *     when the user skipped step 4). Drives the savings rate and the
 *     monthly-organization hint visibility.
 *   - `error` is rendered as an inline `role="alert"` block under the
 *     slider when the page rejected an empty-draft Siguiente.
 */
export interface StepFiveProps {
  /** Current per-purchase spend in CLP; `null` when the slider is untouched. */
  value: number | null;
  /** Called whenever the user moves the slider to a new spend value. */
  onValueChange: (value: number) => void;
  /** Step-4 purchase-frequency selection; `null` if step 4 was skipped. */
  purchaseFrequency: PurchaseFrequency | null;
  /** Optional inline error message displayed beneath the slider. */
  error?: string;
}

/**
 * Canonical question shown above the spend slider.
 */
const STEP_FIVE_QUESTION = "";

/**
 * Canonical label for the Spanish savings copy shown below the slider.
 */


const FREQUENCY_LABELS: Record<PurchaseFrequency, string> = {
  daily: "diaria",
  weekly: "semanal",
  monthly: "mensual",
  other: "actual",
};

/**
 * Formats a CLP amount with the same locale used by the rest of the app.
 * Passed as the `text` prop to `SlotText` so the slot-machine animation
 * works with the correctly formatted string.
 */
const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

export interface SpendRangeConfig {
  minSpend: number;
  midSpend: number;
  maxSpend: number;
}

const DEFAULT_RANGE: SpendRangeConfig = {
  minSpend: 20_000,
  midSpend: 300_000,
  maxSpend: 1_000_000,
};

const MONTHLY_RANGE: SpendRangeConfig = {
  minSpend: 100_000,
  midSpend: 300_000,
  maxSpend: 1_000_000,
};

export const getSpendRangeConfig = (
  frequency: PurchaseFrequency | null,
): SpendRangeConfig => (frequency === "monthly" ? MONTHLY_RANGE : DEFAULT_RANGE);

/**
 * Anchor labels rendered under the slider. Keys are the spend value in CLP;
 * the displayed string uses the Amount formatter so the locale stays in
 * sync with the rest of the app.
 */
const getAnchorLabels = (
  config: SpendRangeConfig,
): ReadonlyArray<{ amount: number; label: string }> => [
  { amount: config.minSpend, label: formatCurrency(config.minSpend) },
  { amount: config.midSpend, label: formatCurrency(config.midSpend) },
  { amount: config.maxSpend, label: `${formatCurrency(config.maxSpend)}+` },
];

/**
 * Internal slider position in the [0, 1000] range. The exponential
 * mapping keeps the curve smooth at both ends so the $10K / $100K /
 * $300K / $1M anchors land in the right thirds.
 */
const SLIDER_MIN = 0;
const SLIDER_MAX = 1000;

/** All slider values are rounded to the nearest $500 for clean UX. */
const SPEND_ROUND_STEP = 500;

/**
 * Annual savings threshold (in CLP) above which the Carriapp Plus
 * requirement indicator MUST be visible. The threshold is strict: an
 * annual savings exactly equal to this value does NOT require Plus.
 */
export const PLUS_THRESHOLD_CLP = 240_000;

const IMPLICIT_CART_LOW_THRESHOLD = 70_000;
const IMPLICIT_CART_HIGH_THRESHOLD = 200_000;
const OTHER_HIGH_SPEND_THRESHOLD = 150_000;

const RATE_LOW = 0.05;
const RATE_MID = 0.10;
const RATE_HIGH = 0.13;
const RATE_OTHER_LOW = 0.07;
const RATE_OTHER_HIGH = 0.10;

export const getMonthlyMultiplier = (freq: PurchaseFrequency | null): number => {
  if (freq === "daily") return 20;
  if (freq === "weekly" || freq === "other") return 4;
  return 1; // monthly, null
};

/**
 * Maps the slider's internal position (0–1000) to a CLP spend value
 * using a piecewise exponential curve anchored at minSpend | midSpend | maxSpend.
 *
 * First half (raw 0–500): minSpend → midSpend
 * Second half (raw 500–1000): midSpend → maxSpend
 * Both halves are rounded to the nearest $500.
 * Exported for unit testing.
 */
export const sliderToSpend = (
  raw: number,
  config: SpendRangeConfig = DEFAULT_RANGE,
): number => {
  const { minSpend, midSpend, maxSpend } = config;
  const HALF = 500;
  let exact: number;
  if (raw <= HALF) {
    const ratio = midSpend / minSpend;
    exact = minSpend * Math.pow(ratio, raw / HALF);
  } else {
    const ratio = maxSpend / midSpend;
    exact = midSpend * Math.pow(ratio, (raw - HALF) / HALF);
  }
  return Math.round(exact / SPEND_ROUND_STEP) * SPEND_ROUND_STEP;
};

/**
 * Inverse of `sliderToSpend` for cases where the page re-mounts with a
 * pre-existing value and needs to position the slider thumb.
 */
const spendToSlider = (
  spend: number,
  config: SpendRangeConfig = DEFAULT_RANGE,
): number => {
  const { minSpend, midSpend, maxSpend } = config;
  const clamped = Math.max(minSpend, Math.min(maxSpend, spend));
  const HALF = 500;
  if (clamped <= midSpend) {
    const ratio = midSpend / minSpend;
    return Math.round((Math.log(clamped / minSpend) / Math.log(ratio)) * HALF);
  }
  const ratio = maxSpend / midSpend;
  return Math.round(HALF + (Math.log(clamped / midSpend) / Math.log(ratio)) * HALF);
};

export interface SavingsProjection {
  savingsRate: number;
  annualSavings: number;
  requiresPlus: boolean;
}

export const computeSavings = (
  monthlySpend: number,
  frequency: PurchaseFrequency | null,
): SavingsProjection => {
  const isValid =
    typeof monthlySpend === "number" &&
    Number.isFinite(monthlySpend) &&
    monthlySpend > 0;
  const safeSpend = isValid ? monthlySpend : 0;

  let savingsRate: number;
  if (frequency === "other") {
    savingsRate = safeSpend >= OTHER_HIGH_SPEND_THRESHOLD ? RATE_OTHER_HIGH : RATE_OTHER_LOW;
  } else {
    const tripsPerMonth = getMonthlyMultiplier(frequency);
    const implicitCart = isValid ? safeSpend / tripsPerMonth : 0;

    if (implicitCart < IMPLICIT_CART_LOW_THRESHOLD) {
      savingsRate = RATE_LOW;
    } else if (implicitCart < IMPLICIT_CART_HIGH_THRESHOLD) {
      savingsRate = RATE_MID;
    } else {
      savingsRate = RATE_HIGH;
    }
  }

  const annualSavings = safeSpend * savingsRate * 12;
  const requiresPlus = annualSavings > PLUS_THRESHOLD_CLP;

  return {
    savingsRate,
    annualSavings,
    requiresPlus,
  };
};

/**
 * Renders the gradient "+Plus" pill used by the Carriapp Plus
 * requirement indicator. Visually consistent with the main header but
 * intentionally NOT extracted to a shared component: the onboarding
 * context has different sizing, the header will evolve independently,
 * and extracting would force a cross-module dependency for ~10 lines.
 */
const PlusBadge = (): JSX.Element => (
  <span
    className="inline-flex h-6 items-center rounded-full border border-border/50 bg-background px-2 text-base font-semibold leading-none"
    data-testid="step-five-plus-badge"
  >
    <span className="bg-[linear-gradient(90deg,#F9802D_0%,#F9802D_42%,#5CA273_100%)] bg-[length:220%_220%] bg-clip-text text-transparent animate-[ai-border-flow_8s_cubic-bezier(0.4,0,0.2,1)_infinite]">
      +Plus
    </span>
  </span>
);

/**
 * Step 5 of the onboarding wizard — per-purchase spend slider + savings
 * projection.
 *
 * Renders a controlled shadcn `Slider` with an exponential mapping
 * (see `sliderToSpend`), a Spanish question that gives the slider context,
 * a savings label paired with `<Amount />` below the slider, the Carriapp
 * Plus requirement indicator when savings cross the $150K threshold, and a
 * low-spend monthly-organization hint when the user is a daily/weekly
 * shopper with sub-$300K spend.
 *
 * The component is fully controlled: it never owns the spend state, never
 * computes a default, and never reads from `localStorage`. The page
 * (`OnboardingPage`) owns the draft, validates on Siguiente, and
 * persists via `writeOnboardingState`.
 */
const StepFive = ({
  value,
  onValueChange,
  purchaseFrequency,
  error,
}: StepFiveProps): JSX.Element => {
  const rangeConfig = getSpendRangeConfig(purchaseFrequency);

  // The slider always renders a numeric position. When the parent draft
  // is null we default to the minimum so the user sees a clean starting
  // state and the headline shows the floor projection.
  const defaultStart = purchaseFrequency === "monthly" ? 100_000 : 295_000;
  const sliderValue = value === null ? defaultStart : value;
  const rawPosition = spendToSlider(sliderValue, rangeConfig);

  const effectiveMonthly = sliderValue * getMonthlyMultiplier(purchaseFrequency);

  // Notify the parent of the default value on first mount when the user
  // hasn't interacted yet, so clicking "Siguiente" without touching the
  // slider accepts the visual default instead of requiring a drag.
  const hasNotifiedDefault = useRef(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (value === null && !hasNotifiedDefault.current) {
      hasNotifiedDefault.current = true;
      onValueChange(sliderValue);
    }
  }, []);

  const projection = computeSavings(effectiveMonthly, purchaseFrequency);

  // Track the savings amount shown on screen — frozen during drag,
  // updated only on slider release so `SlotText` animates the roll.
  const [animatedSavings, setAnimatedSavings] = useState(projection.annualSavings);
  const directionRef = useRef<"up" | "down">("up");

  const handleSliderChange = (next: number[]): void => {
    const nextSpend = sliderToSpend(next[0] ?? SLIDER_MIN, rangeConfig);
    onValueChange(nextSpend);
  };

  const handleSliderCommit = (next: number[]): void => {
    const nextSpend = sliderToSpend(next[0] ?? SLIDER_MIN, rangeConfig);
    const nextMonthly = nextSpend * getMonthlyMultiplier(purchaseFrequency);
    const nextProjection = computeSavings(nextMonthly, purchaseFrequency);
    directionRef.current =
      animatedSavings < nextProjection.annualSavings ? "up" : "down";
    setAnimatedSavings(nextProjection.annualSavings);
  };

  return (
    <section className="flex flex-col gap-14" aria-label="Paso 5">
      <div className="flex flex-col gap-6">
        <h1 className="text-2xl font-semibold leading-tight text-foreground">
          ¿Cuál es tu gasto aproximado <span className="text-primary">por compra</span>?
        </h1>
        <div
          className="text-center text-3xl font-bold text-foreground"
          data-testid="step-five-spend-amount"
        >
          <Amount amount={sliderValue} currency="CLP" locale="es-CL" />
        </div>
        <div className="flex flex-col gap-2" data-testid="step-five-slider">
          <Slider
            min={SLIDER_MIN}
            max={SLIDER_MAX}
            step={1}
            value={[rawPosition]}
            onValueChange={handleSliderChange}
            onValueCommit={handleSliderCommit}
            aria-label="Gasto por compra en CLP"
          />
        <div
          className="relative w-full text-xs text-muted-foreground select-none"
          data-testid="step-five-slider-anchors"
        >
          {getAnchorLabels(rangeConfig).map((anchor) => {
            const pct = (spendToSlider(anchor.amount, rangeConfig) / SLIDER_MAX) * 100;
            const isFirst = pct === 0;
            const isLast = pct === 100;
            return (
              <span
                key={anchor.label}
                className={cn(
                  "absolute",
                  isFirst && "left-0",
                  isLast && "right-0",
                  !isFirst && !isLast && "-translate-x-1/2",
                )}
                style={!isFirst && !isLast ? { left: `${pct}%` } : undefined}
              >
                {anchor.label}
              </span>
            );
          })}
        </div>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1 items-center">
          <p className="text-base font-medium text-muted-foreground text-center">
            Con tu frecuencia {purchaseFrequency ? FREQUENCY_LABELS[purchaseFrequency] : "actual"}, podrías ahorrar:
          </p>
          <div
            className="text-5xl font-bold text-primary"
            data-testid="step-five-headline-amount"
          >
          <SlotText
            text={formatCurrency(animatedSavings)}
            options={{ direction: directionRef.current, duration: 600 }}
          />
            <span className="text-base font-medium text-muted-foreground">
              {" "}al año
            </span>
          </div>
          {animatedSavings > PLUS_THRESHOLD_CLP && (
            <p className="text-sm text-muted-foreground mt-1">
              <Amount
                amount={PLUS_THRESHOLD_CLP}
                currency="CLP"
                locale="es-CL"
              />{" "}con el plan gratuito
            </p>
          )}
            <p className="text-xs text-muted-foreground mt-1 text-center">
              *Usuarios con gasto similar ahorran en promedio{" "}
              <Amount amount={Math.round(projection.annualSavings / 12)} currency="CLP" locale="es-CL" className="font-semibold" />
              {" "}al mes - equivalente a{" "}
              <Amount amount={projection.annualSavings} currency="CLP" locale="es-CL" />
              {" "}al año
            </p>
        </div>
      <AnimatePresence>
        {animatedSavings > PLUS_THRESHOLD_CLP && (
          <motion.div
            layout
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
          >
            <Alert
              className="bg-orange-100/30 p-3"
              data-testid="step-five-plus-indicator"
            >
              <Lightbulb className="w-3"/>
              <AlertTitle className="ml-2">
                Y si te suscribes a Carriapp <PlusBadge />
              </AlertTitle>
              <div />
              <AlertDescription className="flex gap-2 mt-3 mx-auto">
                <span className="font-semibold text-2xl line-through">
                  <Amount
                    amount={PLUS_THRESHOLD_CLP}
                    currency="CLP"
                    locale="es-CL"
                  />
                </span>
                <span>
                  <ArrowRight />
                </span>
                <p>
                  <span className="font-semibold text-2xl text-primary">
                    <SlotText
                      text={formatCurrency(animatedSavings)}
                      options={{ direction: directionRef.current, duration: 600 }}
                    />
                  </span>
                  <span className="text-sm font-medium text-muted-foreground">
                    {" "}al año
                  </span>
                </p>
              </AlertDescription>
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>
        {error ? (
          <p
            role="alert"
            className="text-sm text-destructive"
            data-testid="step-five-error"
          >
            {error}
          </p>
        ) : null}
      </div>
    </section>
  );
};

export default StepFive;
